import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const login = (email: string, password: string) =>
  api.post('/auth/login', { email, password });
export const getUsuarios = () => api.get('/auth/usuarios');

// Prospectos
export const getProspectos = (params?: { estatus?: string; centroDeInfluencia?: string; contactado?: boolean; agenteId?: number; agenciaId?: number }) =>
  api.get('/prospectos', { params });
export const createProspecto = (data: any) => api.post('/prospectos', data);
export const importarProspectosExcel = (archivo: File) => {
  const formData = new FormData();
  formData.append('archivo', archivo);
  return api.post('/prospectos/importar-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const updateProspecto = (id: number, data: any) => api.put(`/prospectos/${id}`, data);
export const convertirProspecto = (id: number, data: { producto?: string; numeroPolizaCliente?: string }) =>
  api.patch(`/prospectos/${id}/convertir`, data);
export const updateEstatusProspecto = (id: number, estatus: string) =>
  api.patch(`/prospectos/${id}/estatus`, { estatus });
export const getProspectoDetalle = (id: number) => api.get(`/prospectos/${id}/detalle`);
export const getProspectosMetricas = (params?: { agenteId?: number; agenciaId?: number }) =>
  api.get('/prospectos/metricas', { params });

// Clientes
export const getClientes = (search?: string) =>
  api.get('/clientes', { params: { search } });
export const getCliente = (id: number) => api.get(`/clientes/${id}`);
export const createCliente = (data: any) => api.post('/clientes', data);
export const updateCliente = (id: number, data: any) => api.put(`/clientes/${id}`, data);
export const getSeguimientoAnualPendiente = () => api.get('/clientes/seguimiento-anual-pendiente');
export const registrarReunionAnual = (id: number) => api.patch(`/clientes/${id}/reunion-anual`);

// Pólizas
export const getPolizas = (params?: any) => api.get('/polizas', { params });
export const getPolizasPorVencer = (dias?: number) =>
  api.get('/polizas/por-vencer', { params: { dias } });
export const createPoliza = (data: any) => api.post('/polizas', data);

// Citas
export const getCitas = (desde?: string, hasta?: string) =>
  api.get('/citas', { params: { desde, hasta } });
export const createCita = (data: any) => api.post('/citas', data);
export const updateEstatusCita = (id: number, estatus: string) =>
  api.patch(`/citas/${id}/estatus`, JSON.stringify(estatus), {
    headers: { 'Content-Type': 'application/json' }
  });

// Seguimientos
export const getSeguimientosPendientes = () => api.get('/seguimientos/pendientes');
export const getSeguimientosByCliente = (clienteId: number) =>
  api.get(`/seguimientos/cliente/${clienteId}`);
export const getSeguimientosByProspecto = (prospectoId: number) =>
  api.get(`/seguimientos/prospecto/${prospectoId}`);
export const createSeguimiento = (data: any) => api.post('/seguimientos', data);

// Cotizaciones
export const getCotizaciones = () => api.get('/cotizaciones');
export const createCotizacion = (data: any) => api.post('/cotizaciones', data);
export const updateEstatusCotizacion = (id: number, estatus: string) =>
  api.patch(`/cotizaciones/${id}/estatus`, JSON.stringify(estatus), {
    headers: { 'Content-Type': 'application/json' }
  });

// Catálogo
export const getAseguradoras = () => api.get('/catalogo/aseguradoras');
export const getRamos = () => api.get('/catalogo/ramos');

// Renovaciones
export const getRenovaciones = () => api.get('/renovaciones');

// Reportes
export const getDashboard = () => api.get('/reportes/dashboard');
export const getRendimientoAgentes = (mes: number, anio: number, agenciaId?: number) =>
  api.get('/reportes/agentes', { params: { mes, anio, agenciaId } });
export const getPipeline = () => api.get('/reportes/pipeline');
export const getPolizasPorRamo = (agenciaId?: number) =>
  api.get('/reportes/polizas-por-ramo', { params: { agenciaId } });
export const getMetasMes = (mes?: number, anio?: number, agenciaId?: number, agenteId?: number) =>
  api.get('/reportes/metas-mes', { params: { mes, anio, agenciaId, agenteId } });
export const updateMetas = (data: any) => api.put('/reportes/metas', data);
export const getMetasAgentes = (mes?: number, anio?: number, agenciaId?: number) =>
  api.get('/reportes/metas-agentes', { params: { mes, anio, agenciaId } });
export const updateMetaAgente = (data: any) => api.put('/reportes/metas-agente', data);
export const getGraficaActividad = (meses?: number) =>
  api.get('/reportes/grafica-actividad', { params: { meses } });
export const exportarReporteExcel = (mes: number, anio: number) =>
  api.get('/reportes/exportar-excel', { params: { mes, anio }, responseType: 'blob' });

// Demo seeder
export const seedDemo = () => api.post('/demo/seed');

// Poliza detalle
export const getPoliza = (id: number) => api.get(`/polizas/${id}`);

// Operaciones
export const getOperaciones = () => api.get('/operaciones');
export const descargarExcelOperacion = (id: number) =>
  api.get(`/operaciones/${id}/descargar`, { responseType: 'blob' });

// Agencias
export const getAgencias = () => api.get('/agencias');

// Usuarios (SuperAdmin)
export const getUsuariosTodos = () => api.get('/usuarios');
export const createUsuario = (data: any) => api.post('/usuarios', data);
export const updateUsuario = (id: number, data: any) => api.put(`/usuarios/${id}`, data);
export const deleteUsuario = (id: number) => api.delete(`/usuarios/${id}`);
export const generarAccesoUsuario = (id: number) => api.post(`/usuarios/${id}/generar-acceso`);

// Reclutamiento
export const getCandidatos = (params?: { estatus?: string; agenteId?: number }) =>
  api.get('/reclutamiento', { params });
export const createCandidato = (data: any) => api.post('/reclutamiento', data);
export const updateCandidato = (id: number, data: any) => api.put(`/reclutamiento/${id}`, data);
export const updateEstatusCandidato = (id: number, estatus: string) =>
  api.patch(`/reclutamiento/${id}/estatus`, { estatus });
export const deleteCandidato = (id: number) => api.delete(`/reclutamiento/${id}`);

// Agente portal
export const getMisPolizas = (tipo: 'pendiente' | 'anulada' | 'vigor' | 'todas' = 'pendiente') =>
  api.get('/agente/polizas', { params: { tipo } });
export const updateTelefonoPoliza = (id: number, telefono: string) =>
  api.patch(`/agente/polizas/${id}/telefono`, { telefono });
export const getAgenteResumen = () => api.get('/agente/resumen');
export const getMiMeta = (mes?: number, anio?: number) =>
  api.get('/agente/meta', { params: { mes, anio } });
export const getHoy = () => api.get('/agente/hoy');
export const getSeguimientoPoliza = (id: number) => api.get(`/agente/polizas/${id}/seguimiento`);
export const upsertSeguimientoPoliza = (id: number, data: {
  enSeguimiento: boolean;
  tipoContacto: string | null;
  notas: string | null;
  fechaUltimoContacto: string | null;
}) => api.put(`/agente/polizas/${id}/seguimiento`, data);

// WhatsApp
export const getWhatsAppQR = () => api.get('/whatsapp/qr');
export const getWhatsAppEstado = () => api.get('/whatsapp/estado');
export const enviarWhatsApp = (mensajes: { telefono: string; texto: string }[]) =>
  api.post('/whatsapp/enviar', { mensajes });
export const desconectarWhatsApp = () => api.post('/whatsapp/desconectar');
